import React from "react";
import { ROUND_LABEL } from "@/lib/api";
import { motion } from "framer-motion";

function TeamRow({ name, score, won, lost }) {
  return (
    <div
      className={`flex items-center justify-between px-3 py-2 ${
        won ? "bg-[#00F0FF]/10" : ""
      }`}
    >
      <span
        className={`font-body text-sm truncate ${
          won
            ? "text-[#00F0FF] font-semibold"
            : lost
            ? "text-white/40 line-through"
            : "text-white"
        }`}
      >
        {name || (
          <span className="text-white/30 uppercase tracking-wider text-xs">
            A definir
          </span>
        )}
      </span>
      <span
        className={`ml-3 grid h-6 min-w-[1.75rem] place-items-center text-xs font-mono ${
          won
            ? "bg-[#00F0FF] text-black"
            : lost
            ? "bg-white/5 text-white/40"
            : "bg-white/10 text-white"
        }`}
      >
        {score ?? "—"}
      </span>
    </div>
  );
}

function MatchNode({ match, accent = "#00F0FF" }) {
  const isLive = match.status === "live";
  const isFinished = match.status === "finished";
  const t1Won = isFinished && match.winner && match.winner === match.team1;
  const t2Won = isFinished && match.winner && match.winner === match.team2;

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.3 }}
      data-testid={`match-${match.id}`}
      className={`relative border ${
        isLive
          ? "border-[#FF3B30]"
          : isFinished
          ? "border-[#00F0FF]/40"
          : "border-white/10"
      } bg-[#141414] divide-y divide-white/5`}
    >
      {isLive && (
        <span className="absolute -top-2 right-2 bg-[#FF3B30] px-2 py-0.5 text-[10px] uppercase tracking-wider text-white pulse-live">
          AO VIVO
        </span>
      )}
      <TeamRow name={match.team1} score={match.score1} won={t1Won} lost={t2Won} />
      <TeamRow name={match.team2} score={match.score2} won={t2Won} lost={t1Won} />
    </motion.div>
  );
}

export default function Bracket({ matches, accent = "#00F0FF" }) {
  if (!matches || matches.length === 0) {
    return (
      <div className="border border-dashed border-white/10 bg-[#141414]/40 stripes-empty p-12 text-center text-white/50 uppercase tracking-[0.2em] text-sm">
        Bracket ainda não disponível
      </div>
    );
  }

  // group by round
  const order = ["R32", "R16", "QF", "SF", "F"];
  const byRound = {};
  for (const m of matches) {
    if (!byRound[m.round]) byRound[m.round] = [];
    byRound[m.round].push(m);
  }
  for (const r of Object.keys(byRound)) {
    byRound[r].sort((a, b) => a.slot - b.slot);
  }
  const rounds = order.filter((r) => byRound[r]);

  return (
    <div
      data-testid="bracket-view"
      className="overflow-x-auto pb-4 -mx-5 sm:-mx-8 px-5 sm:px-8"
    >
      <div
        className="grid gap-6 min-w-[720px]"
        style={{ gridTemplateColumns: `repeat(${rounds.length}, minmax(220px, 1fr))` }}
      >
        {rounds.map((r) => (
          <div key={r} className="flex flex-col">
            <div className="mb-3 flex items-center gap-2">
              <span
                className="h-px flex-1"
                style={{ backgroundColor: accent, opacity: 0.4 }}
              />
              <span
                className="text-[10px] font-semibold uppercase tracking-[0.25em]"
                style={{ color: accent }}
              >
                {ROUND_LABEL[r]}
              </span>
              <span
                className="h-px flex-1"
                style={{ backgroundColor: accent, opacity: 0.4 }}
              />
            </div>
            <div className="bracket-col gap-4 flex-1">
              {byRound[r].map((m) => (
                <MatchNode key={m.id} match={m} accent={accent} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
